import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, ChevronDown } from 'lucide-react'
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n'
import { cn } from '@/lib/utils'

export function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const currentLang = (i18n.language?.split('-')[0] || 'en') as SupportedLanguage
  const current = supportedLanguages[currentLang] || supportedLanguages.en

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const changeLanguage = (lang: SupportedLanguage) => {
    i18n.changeLanguage(lang)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-body-sm text-neutral-300 transition-colors hover:bg-neutral-800 hover:text-white"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="h-4 w-4" />
        <span className="hidden sm:inline">{current.name}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-[140px] rounded-lg border border-neutral-700 bg-neutral-900 py-1 shadow-xl">
          {Object.entries(supportedLanguages).map(([code, { name, flag }]) => (
            <button
              key={code}
              onClick={() => changeLanguage(code as SupportedLanguage)}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-body-sm transition-colors",
                currentLang === code
                  ? "bg-accent-400/10 text-accent-400"
                  : "text-neutral-300 hover:bg-neutral-800"
              )}
            >
              <span>{flag}</span>
              <span>{name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LanguageSwitcher
